import { ChiroUpJSON } from './ChiroUpJSON';

/**
 * Returns a deep-cloned, sanitized copy of encounterInfo with large nested
 * data removed. These fields are vestigial in V2 collaborative encounters —
 * survey data is stored in DynamoDB and accessed via complaintSummaries, not
 * complaint objects. Stripping them prevents Yjs document bloat.
 *
 * The original input is never mutated.
 *
 * Fields removed from complaints:
 * - survey: Full PatientSurvey objects (very large)
 * - surveyQuestions: Nested question arrays with full survey metadata
 * - questions: Survey questions with full metadata
 *
 * Also removes:
 * - Nested encounterInfo.encounterInfo (circular reference bug)
 */

const COMPLAINT_BLOAT_FIELDS = ['survey', 'surveyQuestions', 'questions'];

export function sanitizeEncounterInfo<T extends Record<string, unknown>>(
  encounterInfo: T | undefined
): T | undefined {
  if (!encounterInfo) return encounterInfo;

  // Break circular reference before cloning so JSON.stringify doesn't throw.
  const { encounterInfo: _circular, ...safe } = encounterInfo;

  const result = ChiroUpJSON.clone(safe) as T;
  if (!result) return undefined;

  // Remove bloat fields from complaints
  if (Array.isArray(result.complaints)) {
    for (const complaint of result.complaints) {
      if (!complaint || typeof complaint !== 'object') continue;

      for (const field of COMPLAINT_BLOAT_FIELDS) {
        delete (complaint as Record<string, unknown>)[field];
      }
    }
  }

  return result;
}
